import { useState, useCallback } from 'react';

// ----------------------------------------------------------------------

type QueryParams = {
  orderBy: string;
  page: number;
  rowsPerPage: number;
  globalSearch: string;
  fieldSearch: Record<string, string>;
  order: string;
  dateTimeFiltre?: any;
  advanceFilter?: any[];
  TabFilter?: string;
};

type DefaultFilter = {
  filters: { fieldname: string; search?: string; sort?: number; range?: { from: any; to: any } }[];
  globalSearch: string;
  page: number;
  limit: number;
  combineFieldsGroups?: string[][];
};

type UseTableConfigProps = {
  moduleName: string;
  fields: string[];
  defaultSort?: string;
  combineFields?: string[][];
  fieldMap?: Record<string, string>; // Map field names for API (e.g., { status: 'campaignStatus' })
  customTransform?: (filters: any[], newParams: QueryParams) => any[]; // Custom filter transformation
};

// ----------------------------------------------------------------------

export function useTableConfig({
  moduleName,
  fields,
  defaultSort = 'createdAt',
  combineFields,
  fieldMap = {},
  customTransform,
}: UseTableConfigProps) {
  let initialFilters: { fieldname: string; search?: string; sort?: number }[] = [];

  if (customTransform) {
    initialFilters = customTransform(initialFilters, {} as QueryParams);
  }

  initialFilters.push({ fieldname: defaultSort, sort: 1 });

  const defaultFilter: DefaultFilter = {
    filters: initialFilters,
    globalSearch: '',
    page: 1,
    limit: 10,
    ...(combineFields ? { combineFieldsGroups: combineFields } : {}),
  };

  const [loader, setLoader] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [pageLimit, setPageLimit] = useState(10);
  const [queryParams, setQueryParams] = useState<DefaultFilter>(defaultFilter);

  const handleQueryChange = useCallback(
    (newParams: QueryParams) => {
      setPageLimit(newParams.rowsPerPage);

      const transformedData: DefaultFilter = {
        filters: fields.reduce(
          (acc, field) => {
            if (!field) return acc;

            // Apply field mapping if provided
            const mappedField = fieldMap[field] || field;

            const filterObj: { fieldname: string; search?: string; sort?: number } = {
              fieldname: mappedField,
            };

            const hasSearch =
              newParams.fieldSearch[field] !== undefined && newParams.fieldSearch[field] !== '';
            const hasSort = field === newParams.orderBy;

            if (hasSearch) {
              filterObj.search = newParams.fieldSearch[field];
            }

            if (hasSort) {
              filterObj.sort = newParams.order === 'asc' ? 0 : 1;
            }

            if (hasSearch || hasSort) {
              acc.push(filterObj);
            }

            return acc;
          },
          [] as { fieldname: string; search?: string; sort?: number }[]
        ),
        globalSearch: newParams.globalSearch || '',
        page: newParams.page + 1,
        limit: newParams.rowsPerPage,
      };

      // Apply custom transformation if provided
      if (customTransform) {
        transformedData.filters = customTransform(transformedData.filters, newParams);
      }

      // Add default sort
      transformedData.filters.push({ fieldname: defaultSort, sort: 1 });

      // Add date/time filters
      if (newParams?.dateTimeFiltre && newParams.dateTimeFiltre.length > 0) {
        const dateTimeFilters = newParams.dateTimeFiltre
          .filter((dateTimeField: any) => dateTimeField?.fromDate || dateTimeField?.toDate)
          .map((dateTimeField: any) => ({
            fieldname: dateTimeField.id ?? '',
            range: {
              from: dateTimeField?.fromDate,
              to: dateTimeField?.toDate,
            },
          }));
        transformedData.filters.push(...dateTimeFilters);
      }

      // Add advance filters
      if (newParams?.advanceFilter && newParams.advanceFilter.length > 0) {
        transformedData.filters.push(...newParams.advanceFilter);
      }

      // Add Tab filter
      if (newParams?.TabFilter && newParams.TabFilter !== 'ALL') {
        transformedData.filters.push({ fieldname: 'status', search: newParams.TabFilter });
      }

      // Add combine fields if provided
      if (combineFields) {
        transformedData.combineFieldsGroups = combineFields;
      }

      setQueryParams((prevState) => {
        const prevData = JSON.stringify(prevState);
        const newData = JSON.stringify(transformedData);
        return prevData !== newData ? transformedData : prevState;
      });
    },
    [fields, defaultSort, combineFields, fieldMap, customTransform]
  );

  return {
    // State
    loader,
    setLoader,
    totalCount,
    setTotalCount,
    pageLimit,
    queryParams,

    // Props for TableCustom
    tableToolbar: true,
    totalRecordLength: totalCount,
    onQueryChange: handleQueryChange,
    sortFldList: fields,
    fields,
  };
}
